import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ListingCard from '../components/ListingCard';
import './Listings.css';

const US_STATES = [
  '', 'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY','DC'
];

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'street_legal', label: 'Street Legal' },
  { value: 'mini_moto',    label: 'Mini Moto'    },
  { value: 'medium_moto',  label: 'Medium Moto'  },
  { value: 'mega_moto',    label: 'Mega Moto'    },
  { value: 'part',         label: 'Part'         },
  { value: 'accessory',    label: 'Accessory'    },
];

const CONDITIONS = [
  { value: '', label: 'Any Condition' },
  { value: 'new',        label: 'New'        },
  { value: 'like_new',   label: 'Like New'   },
  { value: 'good',       label: 'Good'       },
  { value: 'fair',       label: 'Fair'       },
  { value: 'parts_only', label: 'Parts Only' },
];

const STAGES = [
  { value: '', label: 'All Stages' },
  { value: '1', label: 'Stage 1 — Stock'  },
  { value: '2', label: 'Stage 2 — Basic'  },
  { value: '3', label: 'Stage 3 — Dialed' },
  { value: '4', label: 'Stage 4 — Built'  },
  { value: '5', label: 'Stage 5 — Tank'   },
];

const SORTS = [
  { value: 'newest',     label: 'Newest First'         },
  { value: 'oldest',     label: 'Oldest First'         },
  { value: 'price_asc',  label: 'Price: Low to High'   },
  { value: 'price_desc', label: 'Price: High to Low'   },
];

export default function Listings() {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);

  const [filters, setFilters] = useState({
    q:        searchParams.get('q')        || '',
    category: searchParams.get('category') || '',
    condition: '',
    minPrice:  '',
    maxPrice:  '',
    state:     '',
    zip:       '',
    sort:      'newest',
    stage:     '',
  });

  const fetchListings = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('listings')
      .select('*', { count: 'exact' })
      .eq('is_active', true);

    if (filters.q)         q = q.ilike('title', `%${filters.q}%`);
    if (filters.category)  q = q.eq('category', filters.category);
    if (filters.condition) q = q.eq('condition', filters.condition);
    if (filters.minPrice)  q = q.gte('price', filters.minPrice);
    if (filters.maxPrice)  q = q.lte('price', filters.maxPrice);
    if (filters.state)     q = q.ilike('state', filters.state);
    if (filters.zip)       q = q.ilike('zip_code', `${filters.zip}%`);
    if (filters.stage)     q = q.eq('stage', parseInt(filters.stage));

    switch (filters.sort) {
      case 'oldest':     q = q.order('created_at', { ascending: true  }); break;
      case 'price_asc':  q = q.order('price',      { ascending: true  }); break;
      case 'price_desc': q = q.order('price',      { ascending: false }); break;
      default:           q = q.order('created_at', { ascending: false });
    }

    const { data, count } = await q.limit(60);
    setListings(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const set = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clear = () => setFilters({ q:'', category:'', condition:'', minPrice:'', maxPrice:'', state:'', zip:'', sort:'newest', stage:'' });
  const hasFilters = filters.q || filters.category || filters.condition || filters.minPrice || filters.maxPrice || filters.state || filters.zip || filters.stage;

  return (
    <div className="listings-page page-enter">
      <div className="container">
        <div className="listings-header">
          <h1 className="listings-heading">Browse Listings</h1>
          <p className="listings-count mono">{total} result{total !== 1 ? 's' : ''}</p>
        </div>

        <div className="listings-layout">
          {/* Sidebar */}
          <aside className="filters-sidebar">
            <div className="filters-top">
              <span className="filters-label">Filters</span>
              {hasFilters && <button className="btn btn-ghost btn-sm" onClick={clear}>Clear</button>}
            </div>

            <form onSubmit={e => { e.preventDefault(); fetchListings(); }}>
              <div className="form-group">
                <label className="form-label">Search</label>
                <input type="text" className="form-input" placeholder="Keywords..." value={filters.q} onChange={e => set('q', e.target.value)} />
              </div>
            </form>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={filters.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-select" value={filters.condition} onChange={e => set('condition', e.target.value)}>
                {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Build Stage</label>
              <select className="form-select" value={filters.stage} onChange={e => set('stage', e.target.value)}>
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="filter-section-divider">Location</div>

            <div className="form-group">
              <label className="form-label">State</label>
              <select className="form-select" value={filters.state} onChange={e => set('state', e.target.value)}>
                <option value="">Any State</option>
                {US_STATES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">ZIP Code</label>
              <input type="text" className="form-input" placeholder="e.g. 90210" maxLength={5} value={filters.zip} onChange={e => set('zip', e.target.value.replace(/\D/g,''))} />
            </div>

            <div className="filter-section-divider">Price</div>

            <div className="price-row">
              <div className="form-group" style={{flex:1, marginBottom:0}}>
                <label className="form-label">Min $</label>
                <input type="number" className="form-input" placeholder="0" value={filters.minPrice} onChange={e => set('minPrice', e.target.value)} />
              </div>
              <div className="form-group" style={{flex:1, marginBottom:0}}>
                <label className="form-label">Max $</label>
                <input type="number" className="form-input" placeholder="Any" value={filters.maxPrice} onChange={e => set('maxPrice', e.target.value)} />
              </div>
            </div>
          </aside>

          {/* Main */}
          <main className="listings-main">
            <div className="sort-bar">
              <select className="form-select sort-select" value={filters.sort} onChange={e => set('sort', e.target.value)}>
                {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="spinner" />
            ) : listings.length > 0 ? (
              <div className="listings-grid">
                {listings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            ) : (
              <div className="empty-state">
                <h3>Nothing found</h3>
                <p>Try adjusting your filters.</p>
                {hasFilters && <button className="btn btn-outline" style={{ marginTop: 20 }} onClick={clear}>Clear Filters</button>}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
